// src/pages/AdminApproveTeachers.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useTeachersApproval } from "../contexts/TeachersApprovalContext";
import { TEACHER_DOCUMENT_TYPES, SUPPLIER_DOCUMENT_TYPES } from "../contexts/DocumentsContext";
import SpinnerOverlay from "../components/ui/SpinnerOverlay";
import AdminApproveTeachersHeader from "../components/ui/AdminApproveTeachersHeader";

/* ===================== Helpers ===================== */

// Which document list applies to this row — the actual point of this
// whole change. A supplier and a teacher share one list/one page, but
// each row shows only the documents relevant to its own role.
const documentTypesFor = (entity) =>
  entity?.role === "supplier" ? SUPPLIER_DOCUMENT_TYPES : TEACHER_DOCUMENT_TYPES;

const norm = (v) => String(v || "").trim();
const isVisibleDoc = (d) => !(d?.hidden === true || d?.hidden === 1);

const getDocByType = (docs, type) => {
  const arr = Array.isArray(docs) ? docs : [];
  const want = norm(type);
  return arr.find((d) => isVisibleDoc(d) && norm(d?.type) === want) || null;
};

const getDocStatusView = (doc) => {
  if (!doc) {
    return { label: "לא מאושר", sub: "לא נשלח", ok: false, tone: "missing", title: "המסמך לא הועלה למערכת" };
  }
  const st = norm(doc?.status).toUpperCase();
  if (st === "APPROVED")       return { label: "מאושר",    sub: "נשלח",                   ok: true,  tone: "approved", title: "המסמך אושר" };
  if (st === "REJECTED")       return { label: "לא מאושר", sub: "נשלח • נדחה",             ok: false, tone: "rejected", title: "המסמך נשלח אך נדחה" };
  if (st === "NEEDS_REVISION") return { label: "לא מאושר", sub: "נשלח • צריך תיקון",       ok: false, tone: "revision", title: "המסמך נשלח וצריך תיקון" };
  return                               { label: "לא מאושר", sub: "נשלח • ממתין לבדיקה",    ok: false, tone: "waiting",  title: "המסמך נשלח וממתין לבדיקה" };
};

// A row's effective status depends on its role: a teacher has the
// review-started quirk (see comment below); a supplier's
// supplierStatus is already the real, final value with no such quirk.
const effectiveTeacherStatus = (t) => {
  const st = String(t?.teacherStatus || "").toUpperCase();
  if (st === "BLOCKED") return "BLOCKED";
  if (!!t?.teacherReviewStarted) return "PENDING";
  return st || "PENDING";
};

const effectiveStatus = (entity) =>
  entity?.role === "supplier"
    ? String(entity?.supplierStatus || "PENDING").toUpperCase()
    : effectiveTeacherStatus(entity);

// A supplier's "blocked" state is really SUSPENDED — same conceptual
// bucket, different literal value, since supplier_profiles.status has
// no BLOCKED at all.
const isBlockedLike = (status) => status === "BLOCKED" || status === "SUSPENDED";

const displayName = (entity) =>
  entity?.role === "supplier"
    ? (entity?.supplierName || "—")
    : (`${entity?.firstName || ""} ${entity?.lastName || ""}`.trim() || "—");

const cls = (...a) => a.filter(Boolean).join(" ");

/* ===================== Icons ===================== */
const SearchIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
  </svg>
);

const RefreshIcon = ({ spinning }) => (
  <svg className={`w-4 h-4 ${spinning ? "animate-spin" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
  </svg>
);

const UserIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
  </svg>
);

const MailIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
  </svg>
);

const PhoneIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
      d="M3 5a2 2 0 012-2h2.28a2 2 0 011.94 1.515l.62 2.48a2 2 0 01-.57 1.93l-1.3 1.3a16 16 0 007.07 7.07l1.3-1.3a2 2 0 011.93-.57l2.48.62A2 2 0 0121 18.72V21a2 2 0 01-2 2h-1C9.82 23 3 16.18 3 8V5z" />
  </svg>
);

const CheckIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
  </svg>
);

const XIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const ShieldIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 2l7 4v6c0 5-3 9-7 10-4-1-7-5-7-10V6l7-4z" />
  </svg>
);

const SendIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
  </svg>
);

/* ===================== Status UI ===================== */
// One combined map: teacher's 4 states + supplier's SUSPENDED. A
// teacher row never has SUSPENDED and a supplier row never has
// REJECTED/BLOCKED, so there's no real overlap risk.
const getStatusInfo = (status) => {
  const map = {
    PENDING:   { label: "ממתין", bg: "bg-amber-50",   text: "text-amber-700",   border: "border-amber-200"   },
    APPROVED:  { label: "מאושר", bg: "bg-emerald-50",  text: "text-emerald-700", border: "border-emerald-200" },
    REJECTED:  { label: "נדחה",  bg: "bg-red-50",      text: "text-red-700",     border: "border-red-200"     },
    BLOCKED:   { label: "חסום",  bg: "bg-gray-50",     text: "text-gray-700",    border: "border-gray-200"    },
    SUSPENDED: { label: "מושעה", bg: "bg-gray-50",     text: "text-gray-700",    border: "border-gray-200"    },
  };
  return map[String(status || "").toUpperCase()] || map.PENDING;
};

const StatusBadge = ({ status }) => {
  const s = getStatusInfo(status);
  return (
    <span className={`inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-bold ${s.bg} ${s.text} border ${s.border}`}>
      {s.label}
    </span>
  );
};

const VerifyBadge = ({ ok, label }) => (
  <div className={cls(
    "inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-bold border",
    ok ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-gray-50 text-gray-600 border-gray-200"
  )}>
    {ok ? <CheckIcon className="w-3.5 h-3.5" /> : <XIcon className="w-3.5 h-3.5" />}
    <span>{label}</span>
  </div>
);

/* ===================== Validation ===================== */
// The action vocabulary itself never changes per role (see
// decideTeacher()'s translation on the backend) — only whether REJECT
// is a valid choice at all. A supplier has no REJECTED state, so it's
// excluded from the allowed list whenever the selected row is one.
const buildDecisionSchema = (isSupplierRow) =>
  Yup.object({
    action: Yup.string()
      .oneOf(
        isSupplierRow ? ["APPROVE", "BLOCK", "UNBLOCK"] : ["APPROVE", "REJECT", "BLOCK", "UNBLOCK"],
        "נא לבחור פעולה"
      )
      .required("נא לבחור פעולה"),
    notes: Yup.string().when("action", {
      // Teacher: required on reject/block. Supplier: required on suspend.
      is: (a) => a === "BLOCK" || (!isSupplierRow && a === "REJECT"),
      then: (s) => s.trim().required(isSupplierRow ? "בעת השעיה חובה לרשום הערה." : "בעת דחייה/חסימה חובה לרשום הערה."),
      otherwise: (s) => s.notRequired(),
    }),
  });

/* ===================== Page ===================== */
export default function AdminApproveTeachers() {
  const {
    teachers,
    loadTeachers,
    loadTeachersCounts,
    counts,
    filters,
    setFilters,
    page,
    setPage,
    limit,
    total,
    adminDecideTeacher,
    isLoading,
  } = useTeachersApproval();

  const list = Array.isArray(teachers) ? teachers : [];

  const [selected,    setSelected]    = useState(null);
  const [refreshing,  setRefreshing]  = useState(false);
  const [submitting,  setSubmitting]  = useState(false);
  const [searchDraft, setSearchDraft] = useState(filters?.search || "");

  const isSupplierSelected = selected?.role === "supplier";
  const decisionSchema = useMemo(() => buildDecisionSchema(isSupplierSelected), [isSupplierSelected]);

  // Used for mobile scroll-to-details
  const detailsPanelRef = useRef(null);

  const totalPages = useMemo(() => {
    const t = Number(total || 0);
    const l = Number(limit || 6);
    return Math.max(1, Math.ceil(t / l));
  }, [total, limit]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: { action: "", notes: selected?.adminNotes || "" },
    validationSchema: decisionSchema,
    onSubmit: async (values) => {
      if (!selected?.id) return;
      setSubmitting(true);
      try {
        formik.setStatus("");
        const status =
          values.action === "APPROVE" ? "APPROVED"
          : values.action === "REJECT" ? "REJECTED"
          : values.action === "BLOCK"  ? "BLOCKED"
          : "APPROVED"; // UNBLOCK

        // Backend translates BLOCKED->SUSPENDED for a supplier target
        // and ignores notes for one (no admin_notes column) — the
        // frontend doesn't need to know that mapping itself.
        await adminDecideTeacher(selected.id, { status, notes: values.notes || "" });
        await loadTeachers(filters, page, limit);
        await loadTeachersCounts();
        setSelected(null);
        formik.resetForm();
      } catch (e) {
        formik.setStatus(e?.message || "שגיאה בעת עדכון המשתמש");
      } finally {
        setSubmitting(false);
      }
    },
  });

  useEffect(() => {
    loadTeachersCounts();
    loadTeachers(filters, page, limit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadTeachers(filters, page, limit);
  }, [page, limit, filters, loadTeachers]);

  const handleSelect = useCallback((t) => {
    setSelected(t);
    formik.resetForm({ values: { action: "", notes: t?.adminNotes || "" } });
    formik.setStatus("");

    // On mobile/tablet, scroll the details panel into view
    setTimeout(() => {
      detailsPanelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 80);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadTeachers(filters, page, limit);
    await loadTeachersCounts();
    setTimeout(() => setRefreshing(false), 400);
  };

  const goPrev = () => setPage((p) => Math.max(1, Number(p || 1) - 1));
  const goNext = () => setPage((p) => Math.min(totalPages, Number(p || 1) + 1));

  const entityDocsSummary = (t) => {
    const docs = (Array.isArray(t?.documents) ? t.documents : []).filter(isVisibleDoc);
    return {
      totalDocs: docs.length,
      approved: docs.filter((d) => d.status === "APPROVED").length,
      rejected: docs.filter((d) => d.status === "REJECTED").length,
      revision: docs.filter((d) => d.status === "NEEDS_REVISION").length,
      waiting:  docs.filter((d) => d.status === "NO_RESPONSE").length,
    };
  };

  const selectedStatus = effectiveStatus(selected);

  return (
    <div className="min-h-screen" dir="rtl">
      {isLoading && <SpinnerOverlay variant="glow" text="Please wait" dark={false} blur={false} />}

      <AdminApproveTeachersHeader />

      <div className="min-h-screen max-w-7xl mx-auto p-4 sm:p-6">

        {/* ── Filters ── */}
        <div className="bg-white rounded-2xl border border-gray-200 p-4 sm:p-6 md:p-8 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">

            {/* Search — grows to fill */}
            <div className="flex-1 min-w-0">
              <div className="relative">
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
                  <SearchIcon />
                </span>
                <input
                  value={searchDraft}
                  onChange={(e) => setSearchDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      setPage(1);
                      setFilters((p) => ({ ...(p || {}), search: searchDraft, status: "" }));
                    }
                  }}
                  placeholder="חיפוש מורה / ספק..."
                  className="w-full px-4 pr-10 py-2 rounded-lg border border-gray-200 bg-white outline-none transition-all focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
                />
              </div>
            </div>

            {/* Status select — full-width on mobile, auto on sm+ */}
            <select
              value={filters?.status ?? "PENDING"}
              onChange={(e) => setFilters((p) => ({ ...(p || {}), status: e.target.value }))}
              className="w-full sm:w-auto px-4 py-2 rounded-lg border border-gray-200 bg-white outline-none transition-all focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 font-semibold text-gray-700"
            >
              <option value="PENDING">ממתינים</option>
              <option value="APPROVED">מאושרים</option>
              <option value="REJECTED">נדחו</option>
              <option value="BLOCKED">חסומים</option>
              <option value="">הכל</option>
            </select>

            {/* Refresh button — icon + label on sm+, icon-only on xs */}
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="w-full sm:w-auto px-4 py-2 rounded-lg text-sm font-semibold bg-white border border-gray-200 hover:bg-gray-50 disabled:opacity-50 transition-all flex items-center justify-center gap-2"
            >
              <RefreshIcon spinning={refreshing} />
              <span>{refreshing ? "מרענן..." : "רענן"}</span>
            </button>
          </div>
        </div>

        {/* ── KPI Cards ── */}
        {/* 2 cols on xs, 4 cols on sm+ */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-6">
          <div className="bg-gradient-to-r from-amber-50 to-orange-50 p-3 sm:p-4 rounded-xl border border-amber-200">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-9 h-9 sm:w-10 sm:h-10 bg-gradient-to-br from-amber-500 to-orange-500 rounded-lg flex items-center justify-center shadow-sm flex-shrink-0">
                <span className="text-white font-black text-sm">⏳</span>
              </div>
              <div className="min-w-0">
                <p className="text-xs sm:text-sm text-amber-700">ממתינים</p>
                <p className="text-xl sm:text-2xl font-bold text-amber-900">{Number(counts?.PENDING || 0)}</p>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-emerald-50 to-green-50 p-3 sm:p-4 rounded-xl border border-emerald-200">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-9 h-9 sm:w-10 sm:h-10 bg-gradient-to-br from-emerald-500 to-green-500 rounded-lg flex items-center justify-center shadow-sm flex-shrink-0">
                <CheckIcon className="text-white" />
              </div>
              <div className="min-w-0">
                <p className="text-xs sm:text-sm text-emerald-700">מאושרים</p>
                <p className="text-xl sm:text-2xl font-bold text-emerald-900">{Number(counts?.APPROVED || 0)}</p>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-red-50 to-rose-50 p-3 sm:p-4 rounded-xl border border-red-200">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-9 h-9 sm:w-10 sm:h-10 bg-gradient-to-br from-red-500 to-rose-500 rounded-lg flex items-center justify-center shadow-sm flex-shrink-0">
                <XIcon className="w-4 h-4 text-white" />
              </div>
              <div className="min-w-0">
                <p className="text-xs sm:text-sm text-red-700">נדחו</p>
                <p className="text-xl sm:text-2xl font-bold text-red-900">{Number(counts?.REJECTED || 0)}</p>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-gray-50 to-gray-100 p-3 sm:p-4 rounded-xl border border-gray-200">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-9 h-9 sm:w-10 sm:h-10 bg-gradient-to-br from-gray-600 to-gray-800 rounded-lg flex items-center justify-center shadow-sm flex-shrink-0">
                <ShieldIcon className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
              </div>
              <div className="min-w-0">
                <p className="text-xs sm:text-sm text-gray-700">סה״כ</p>
                <p className="text-xl sm:text-2xl font-bold text-gray-900">{Number(counts?.TOTAL || 0)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* ── Main two-panel grid ──
            Stacks on mobile: list first, details below.
            Side-by-side at lg (1024px).
        */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

          {/* ── Teachers List ── */}
          <div>
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
              {/* Panel header */}
              <div className="px-4 sm:px-6 py-4 bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
                  <h2 className="text-base font-bold text-gray-900">רשימת מורים / ספקים</h2>
                  <div className="text-sm text-gray-600 font-medium">
                    עמוד {page} מתוך {totalPages} • {list.length} מורים
                  </div>
                </div>
              </div>

              {/* List body
                  Shorter max-h on mobile so it doesn't push details too far down;
                  full height on lg+ where the two panels sit side-by-side.
              */}
              <div className="max-h-[360px] sm:max-h-[480px] lg:max-h-[620px] overflow-y-auto divide-y divide-gray-100">
                {list.length === 0 ? (
                  <div className="p-12 text-center">
                    <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-sm">
                      <UserIcon className="w-8 h-8 sm:w-10 sm:h-10 text-indigo-500" />
                    </div>
                    <p className="text-gray-800 font-bold text-base sm:text-lg">
                      {isLoading ? "טוען..." : "אין רשומות להצגה"}
                    </p>
                    <p className="text-gray-500 text-sm mt-2">נסה לשנות את הסינון או החיפוש</p>
                  </div>
                ) : (
                  list.map((t) => {
                    const isActive   = selected?.id === t.id;
                    const summary    = entityDocsSummary(t);
                    const effStatus  = effectiveStatus(t);
                    const isPending  = effStatus === "PENDING";

                    return (
                      <div
                        key={t.id}
                        className={cls(
                          "cursor-pointer transition-colors",
                          isActive ? "bg-indigo-50/60 hover:bg-indigo-50/80" : "hover:bg-gray-50"
                        )}
                        onClick={() => handleSelect(t)}
                      >
                        <div className="p-4 sm:p-5">
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className={cls("font-bold text-sm sm:text-base", isActive ? "text-indigo-900" : "text-gray-900")}>
                                  {displayName(t)}
                                </span>
                                {isPending && (
                                  <span className="px-2 py-0.5 bg-red-500 text-white text-xs rounded-full font-bold">חדש</span>
                                )}
                              </div>

                              <p className="text-xs sm:text-sm text-gray-600 font-medium line-clamp-1 break-all mt-1">
                                {t.email || "—"}
                              </p>

                              {/* Docs summary — wraps naturally */}
                              <div className="flex items-center gap-2 flex-wrap text-xs text-gray-600 font-semibold mt-2">
                                <span>מסמכים: {summary.totalDocs}</span>
                                <span className="text-gray-300">•</span>
                                <span className="text-emerald-700">מאושרים: {summary.approved}</span>
                                <span className="text-gray-300">•</span>
                                <span className="text-amber-700">ממתינים: {summary.waiting}</span>
                                <span className="text-gray-300">•</span>
                                <span className="text-red-700">נדחו: {summary.rejected}</span>
                              </div>
                            </div>

                            <div className="flex-shrink-0">
                              <StatusBadge status={effStatus} />
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              {/* Pagination */}
              <div className="px-4 sm:px-6 py-4 bg-gradient-to-r from-gray-50 to-gray-100 border-t border-gray-200">
                <div className="flex items-center justify-between gap-2">
                  <button
                    disabled={page >= totalPages}
                    onClick={goNext}
                    className={cls(
                      "px-3 sm:px-4 py-2 rounded-lg text-sm font-semibold transition-all",
                      page >= totalPages
                        ? "bg-white border border-gray-200 text-gray-400 cursor-not-allowed opacity-60"
                        : "bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400"
                    )}
                  >
                   → הבא 
                  </button>
                  
                  <div className="text-sm text-gray-700 font-semibold">
                    <span className="text-gray-900">{page}</span>
                    <span className="text-gray-400 mx-1">/</span>
                    <span className="text-gray-900">{totalPages}</span>
                  </div>

                  <button
                    disabled={page <= 1}
                    onClick={goPrev}
                    className={cls(
                      "px-3 sm:px-4 py-2 rounded-lg text-sm font-semibold transition-all",
                      page <= 1
                        ? "bg-white border border-gray-200 text-gray-400 cursor-not-allowed opacity-60"
                        : "bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400"
                    )}
                  >
                     הקודם ←
                  </button>

                </div>
              </div>
            </div>
          </div>

          {/* ── Details Panel ── */}
          {/* id/ref used for mobile scroll-into-view */}
          <div ref={detailsPanelRef}>
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl border border-gray-200 shadow-sm overflow-hidden lg:sticky lg:top-6">
              {!selected ? (
                <div className="p-8">
                  <div className="text-center py-8 sm:py-12">
                    <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-sm">
                      <ShieldIcon className="w-8 h-8 sm:w-10 sm:h-10 text-indigo-500" />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">בחר מורה / ספק לצפייה</h3>
                    <p className="text-gray-600 text-sm">לחץ על שורה מהרשימה כדי לצפות בפרטים ובסטטוס המסמכים</p>
                  </div>
                </div>
              ) : (
                <>
                  {/* Details header */}
                  <div className="px-4 sm:px-6 py-4 bg-gradient-to-r from-indigo-50 to-purple-50 border-b border-indigo-100">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <h3 className="text-base sm:text-lg font-bold text-gray-900 truncate">
                          {displayName(selected)}
                        </h3>
                        <p className="text-xs sm:text-sm text-gray-600 truncate">{selected.email || "—"}</p>
                      </div>
                      <button
                        onClick={() => setSelected(null)}
                        className="p-2 rounded-xl hover:bg-white/80 border border-indigo-200 transition-colors flex-shrink-0"
                        title="סגור"
                      >
                        <XIcon />
                      </button>
                    </div>
                  </div>

                  {/* Entity info */}
                  <div className="p-4 sm:p-6 border-b border-gray-200 bg-gradient-to-br from-gray-50 to-white">
                    <h4 className="text-sm font-bold text-gray-900 mb-3">
                      {isSupplierSelected ? "פרטי מרכז הלמידה" : "פרטי המורה"}
                    </h4>
                    <div className="space-y-2.5">
                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <UserIcon className="text-indigo-600" />
                        </div>
                        <span className="font-semibold text-gray-900 text-sm">
                          {displayName(selected)}
                        </span>
                      </div>

                      {isSupplierSelected && selected.contactPerson && (
                        <div className="flex items-center gap-2.5">
                          <div className="w-8 h-8 bg-indigo-50 rounded-lg flex items-center justify-center flex-shrink-0">
                            <UserIcon className="text-indigo-500" />
                          </div>
                          <span className="text-gray-700 text-sm">איש קשר: {selected.contactPerson}</span>
                        </div>
                      )}

                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <MailIcon className="text-purple-600" />
                        </div>
                        <span className="text-gray-700 text-sm truncate">{selected.email || "—"}</span>
                      </div>
                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 bg-pink-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <PhoneIcon className="text-pink-600" />
                        </div>
                        <span className="text-gray-700 text-sm">{(isSupplierSelected ? (selected.supplierPhone || selected.phone) : selected.phone) || "—"}</span>
                      </div>

                      {isSupplierSelected && (
                        <>
                          {selected.businessNumber && (
                            <div className="text-xs text-gray-500 font-semibold pr-10">מספר עוסק: {selected.businessNumber}</div>
                          )}
                          {selected.gefenSupplierNumber && (
                            <div className="text-xs text-gray-500 font-semibold pr-10">מספר ספק בגפ״ן: {selected.gefenSupplierNumber}</div>
                          )}
                          {selected.address && (
                            <div className="text-xs text-gray-500 font-semibold pr-10">כתובת: {selected.address}</div>
                          )}
                        </>
                      )}

                      <div className="pt-1 flex flex-wrap gap-2">
                        <VerifyBadge ok={!!selected.emailConfirmed} label={selected.emailConfirmed ? "מייל מאומת" : "מייל לא מאומת"} />
                        <VerifyBadge ok={!!selected.phoneConfirmed} label={selected.phoneConfirmed ? "טלפון מאומת" : "טלפון לא מאומת"} />
                      </div>
                      <div className="pt-2 flex flex-wrap items-center gap-2">
                        <StatusBadge status={effectiveStatus(selected)} />
                        <span className="text-xs text-gray-500 font-semibold">
                          החלטת אדמין: {(isSupplierSelected ? selected.supplierStatus : selected.teacherStatus) || "PENDING"}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Docs statuses */}
                  <div className="p-4 sm:p-6 border-b border-gray-200">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-sm font-bold text-gray-900">סטטוס מסמכים</h4>
                      <span className="text-xs text-gray-500 font-bold">
                        סה״כ: {(selected.documents || []).length}
                      </span>
                    </div>

                    {!(Array.isArray(selected.documents) && selected.documents.length) ? (
                      <div className="text-sm text-gray-600 font-semibold">אין מסמכים למורה זה.</div>
                    ) : (
                      <div className="space-y-2">
                        {documentTypesFor(selected).map((type) => {
                          const doc  = getDocByType(selected?.documents, type);
                          const view = getDocStatusView(doc);

                          const toneClasses =
                            view.tone === "approved" ? {
                              row:  "border-emerald-200/60 bg-emerald-50/30",
                              icon: "bg-emerald-50 border-emerald-200 text-emerald-700",
                              pill: "bg-emerald-50 text-emerald-700 border-emerald-200",
                            }
                            : view.tone === "revision" ? {
                              row:  "border-amber-200/60 bg-amber-50/30",
                              icon: "bg-amber-50 border-amber-200 text-amber-800",
                              pill: "bg-amber-50 text-amber-800 border-amber-200",
                            }
                            : view.tone === "waiting" ? {
                              row:  "border-gray-200/70 bg-gray-50/40",
                              icon: "bg-gray-50 border-gray-200 text-gray-700",
                              pill: "bg-gray-50 text-gray-700 border-gray-200",
                            }
                            : {
                              row:  "border-red-200/60 bg-red-50/25",
                              icon: "bg-red-50 border-red-200 text-red-700",
                              pill: "bg-red-50 text-red-700 border-red-200",
                            };

                          return (
                            <div
                              key={type}
                              className={cls(
                                "flex items-center justify-between gap-3 p-3 rounded-xl border",
                                "bg-gradient-to-br from-white to-gray-50 transition-all",
                                toneClasses.row
                              )}
                            >
                              <div className="min-w-0">
                                {/* text-md is not a Tailwind class — corrected to text-sm */}
                                <div className="text-sm font-medium leading-none">{type}</div>
                              </div>

                              <div className="flex items-center gap-2 flex-shrink-0">
                                {!view.ok && (
                                  <span
                                    className={cls("px-2.5 py-1 rounded-full text-xs font-bold border whitespace-nowrap", toneClasses.pill)}
                                    title={view.title}
                                  >
                                    {view.sub}
                                  </span>
                                )}
                                <div
                                  className={cls("w-9 h-9 sm:w-10 sm:h-10 rounded-xl border flex items-center justify-center flex-shrink-0", toneClasses.icon)}
                                  title={view.title}
                                >
                                  {view.ok
                                    ? <CheckIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                                    : <XIcon     className="w-4 h-4 sm:w-5 sm:h-5" />}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    <div className="pt-3 mt-3 border-t border-gray-100">
                      <p className="text-xs text-gray-500 font-semibold">
                        טיפ: אפשר להחליט "מאושר" רק אם כל המסמכים מאושרים + מייל/טלפון מאומתים.
                      </p>
                    </div>
                  </div>

                  {/* Admin decision form */}
                  <form onSubmit={formik.handleSubmit} className="p-4 sm:p-6 space-y-4">
                    <div>
                      <h4 className="text-sm font-bold text-gray-900 mb-3">בחר פעולה</h4>

                      {isBlockedLike(selectedStatus) ? (
                        <button
                          type="button"
                          onClick={() => formik.setFieldValue("action", "UNBLOCK")}
                          className={cls(
                            "w-full p-3 rounded-lg border-2 font-semibold transition-all flex items-center justify-center gap-2",
                            formik.values.action === "UNBLOCK"
                              ? "bg-gradient-to-r from-emerald-600 to-green-600 text-white border-emerald-600 shadow-sm"
                              : "border-gray-200 hover:border-emerald-300 hover:bg-emerald-50 text-gray-700"
                          )}
                        >
                          <CheckIcon />
                          <span>{isSupplierSelected ? "בטל השעיה" : "בטל חסימה"}</span>
                        </button>
                      ) : (
                        /* Supplier rows never show REJECT (no such
                           state exists) — 2 cols always for them, same
                           2/3-col teacher logic otherwise. */
                        <div className={cls(
                          "grid gap-2",
                          !isSupplierSelected && selectedStatus === "APPROVED" ? "grid-cols-3" : "grid-cols-2"
                        )}>
                          <button
                            type="button"
                            onClick={() => formik.setFieldValue("action", "APPROVE")}
                            className={cls(
                              "p-2.5 sm:p-3 rounded-lg border-2 text-sm font-semibold transition-all flex items-center justify-center gap-1.5",
                              formik.values.action === "APPROVE"
                                ? "bg-gradient-to-r from-emerald-600 to-green-600 text-white border-emerald-600 shadow-sm"
                                : "border-gray-200 hover:border-emerald-300 hover:bg-emerald-50 text-gray-700"
                            )}
                          >
                            <CheckIcon />
                            <span>אשר</span>
                          </button>

                          {!isSupplierSelected && (
                            <button
                              type="button"
                              onClick={() => formik.setFieldValue("action", "REJECT")}
                              className={cls(
                                "p-2.5 sm:p-3 rounded-lg border-2 text-sm font-semibold transition-all flex items-center justify-center gap-1.5",
                                formik.values.action === "REJECT"
                                  ? "bg-gradient-to-r from-red-600 to-rose-600 text-white border-red-600 shadow-sm"
                                  : "border-gray-200 hover:border-red-300 hover:bg-red-50 text-gray-700"
                              )}
                            >
                              <XIcon className="w-4 h-4" />
                              <span>דחה</span>
                            </button>
                          )}

                          {(isSupplierSelected || selectedStatus === "APPROVED") && (
                            <button
                              type="button"
                              onClick={() => formik.setFieldValue("action", "BLOCK")}
                              className={cls(
                                "p-2.5 sm:p-3 rounded-lg border-2 text-sm font-semibold transition-all flex items-center justify-center gap-1.5",
                                formik.values.action === "BLOCK"
                                  ? "bg-gradient-to-r from-gray-800 to-gray-900 text-white border-gray-800 shadow-sm"
                                  : "border-gray-200 hover:border-gray-400 hover:bg-gray-50 text-gray-700"
                              )}
                            >
                              <XIcon className="w-4 h-4" />
                              <span>{isSupplierSelected ? "השעה" : "חסום"}</span>
                            </button>
                          )}
                        </div>
                      )}

                      {formik.touched.action && formik.errors.action && (
                        <div className="mt-2 text-sm text-red-600 font-semibold">{formik.errors.action}</div>
                      )}
                    </div>

                    {(
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">
                          {isSupplierSelected ? "הערות למרכז הלמידה (יוצגו לו)" : "הערות למורה"}
                          {(formik.values.action === "REJECT" || formik.values.action === "BLOCK") && (
                            <span className="text-red-500 mr-1">*</span>
                          )}
                        </label>
                        <textarea
                          name="notes"
                          value={formik.values.notes}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          placeholder={isSupplierSelected ? "הסבר להחלטת הפלטפורמה, למשל: חסרה תעודת עוסק מורשה / הוחלט להשעות עקב..." : "למשל: חסר אישור ניהול חשבון / המסמך אינו קריא..."}
                          rows={4}
                          className={cls(
                          "w-full px-3 py-2 rounded-lg border-2 outline-none transition-all resize-none font-medium focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400",
                          formik.touched.notes && formik.errors.notes ? "border-red-300 bg-red-50" : "border-gray-200"
                        )}
                      />
                      {formik.touched.notes && formik.errors.notes && (
                        <div className="mt-1 text-sm text-red-600 font-semibold">{formik.errors.notes}</div>
                      )}
                      </div>
                    )}

                    {formik.status && (
                      <div className="bg-red-50 border border-red-200 text-red-800 rounded-xl p-3 text-sm font-medium">
                        {formik.status}
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={submitting || !formik.values.action}
                      className="w-full py-3 rounded-lg bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-bold hover:from-indigo-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm flex items-center justify-center gap-2"
                    >
                      <SendIcon />
                      <span>{submitting ? "שולח..." : "שמור החלטה"}</span>
                    </button>
                  </form>
                </>
              )}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}